// Apolo IDE — useMultiAgentPanel hook (Sprint v4 Epic S5).
//
// Top-level coordination for the MultiAgent Panel UI shell. Owns:
//   - active sub-tab id   ('compare' | 'decompose' | 'history')
//   - dispatch of choice for the History tab (when user clicks a row)
//   - global ⇧⌘A shortcut to toggle panel open + jump to its default tab
//
// Sub-hooks (useCompareMode, useDecomposer, useOrchestrator, useCostMeter)
// stay isolated — this hook only coordinates which tab is visible and how
// the panel wires into the workspace shortcut layer. That keeps each
// concern unit-testable in isolation.

import { useCallback, useEffect, useState } from 'react';

const VALID_TABS = ['compare', 'decompose', 'history'];

function isTypingTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export default function useMultiAgentPanel(options = {}) {
  const initialTab = VALID_TABS.includes(options.initialTab) ? options.initialTab : 'compare';
  const onOpen = options.onOpen || null;

  const [tab, setTabState] = useState(initialTab);
  const [activeDispatchId, setActiveDispatchId] = useState(null);

  const setTab = useCallback((next) => {
    if (!VALID_TABS.includes(next)) return false;
    setTabState(next);
    return true;
  }, []);

  // ⇧⌘A — open MultiAgent Panel. The actual "open" effect is delegated
  // to the host (SessionWorkspace) via `onOpen`, because the panel is a
  // tab in SidePanel; the shortcut needs to both open SidePanel and set
  // the active tab to 'agents'.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function handler(e) {
      if (isTypingTarget(e.target)) return;
      const meta = e.metaKey || e.ctrlKey;
      const shift = e.shiftKey;
      if (!meta || !shift) return;
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        onOpen?.();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen]);

  const openHistoryFor = useCallback((dispatchId) => {
    setActiveDispatchId(dispatchId);
    setTabState('history');
  }, []);

  return {
    tab,
    setTab,
    activeDispatchId,
    setActiveDispatchId,
    openHistoryFor,
    validTabs: VALID_TABS,
  };
}

// Exported so unit tests can match the canonical set.
export { VALID_TABS };
